import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../../services/api';
import { renderError } from '../../../helpers/errors';
import PanelTemplate from '../../../templates/Panel';
import PageHeader from '../../../components/PageHeader';
import Box from '../../../components/Box';
import Button from '../../../components/Button';
import Input from '../../../components/Input';
import Modal from '../../../components/Modal';
import { ModalDelete } from '../../../components/ModalDelete';
import { Loading } from '../../../components/Loading';
import InputColor from '../../../components/InputColor';
import TagChip from '../../../components/TagChip';
import Icon from '../../../components/Icon';
import { DEFAULT_TAG_COLOR } from '../../../helpers/colors';

const normalize = (text) => (text ?? '').trim().toLowerCase();

export const CrmTags = () => {
    const [tags, setTags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState(null);
    const [name, setName] = useState('');
    const [color, setColor] = useState(DEFAULT_TAG_COLOR);
    const [saving, setSaving] = useState(false);
    const [toDelete, setToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [search, setSearch] = useState('');

    const visibleTags = useMemo(() => {
        const term = normalize(search);
        if (term === '') return tags;
        return tags.filter((tag) => normalize(tag.name).includes(term));
    }, [tags, search]);

    const load = () => {
        setLoading(true);
        api.get('/crm-tag')
            .then((res) => setTags(res.data.resources ?? []))
            .catch(renderError)
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
    }, []);

    const openCreate = () => {
        setEditing(null);
        setName('');
        setColor(DEFAULT_TAG_COLOR);
        setShowModal(true);
    };

    const openEdit = (tag) => {
        setEditing(tag);
        setName(tag.name);
        setColor(tag.color);
        setShowModal(true);
    };

    const saveDisabled = saving || name.trim() === '';

    const handleSave = () => {
        setSaving(true);
        const body = { name, color: (color || '').slice(0, 7) };
        const request = editing ? api.put(`/crm-tag/${editing.id}`, body) : api.post('/crm-tag', body);
        request
            .then(() => {
                toast.success(editing ? 'Etiqueta atualizada' : 'Etiqueta criada');
                setShowModal(false);
                load();
            })
            .catch(renderError)
            .finally(() => setSaving(false));
    };

    const handleDelete = () => {
        setDeleting(true);
        api.delete(`/crm-tag/${toDelete.id}`)
            .then(() => {
                toast.success('Etiqueta excluída');
                setToDelete(null);
                load();
            })
            .catch(renderError)
            .finally(() => setDeleting(false));
    };

    return (
        <PanelTemplate id="crm-tags">
            <PageHeader
                left={
                    <div>
                        <h1>Etiquetas</h1>
                        <p className="crm-tags__subtitle">Crie etiquetas coloridas para classificar os cards dos painéis.</p>
                    </div>
                }
                right={
                    <Button type="primary" size="small" svg="plus-white" text="Nova etiqueta" action={openCreate} />
                }
            />

            {loading ? (
                <Loading show={true} />
            ) : tags.length === 0 ? (
                <Box>
                    <div className="crm-tags__empty">
                        <h2>Crie sua primeira etiqueta</h2>
                        <p>Etiquetas ajudam a classificar e filtrar os cards dos seus painéis.</p>
                        <Button type="primary" svg="plus-white" text="Nova etiqueta" action={openCreate} />
                    </div>
                </Box>
            ) : (
                <Box>
                    {tags.length > 5 && (
                        <div className="tags-list__toolbar">
                            <div className="tags-list__search">
                                <Icon name="search" size={16} strokeWidth={2} />
                                <input
                                    type="text"
                                    placeholder="Buscar por nome"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                                {search !== '' && (
                                    <button type="button" aria-label="Limpar busca" onClick={() => setSearch('')}>
                                        <Icon name="close" size={15} strokeWidth={2} />
                                    </button>
                                )}
                            </div>
                            <span className="tags-list__count">
                                {visibleTags.length} de {tags.length} {tags.length === 1 ? 'etiqueta' : 'etiquetas'}
                            </span>
                        </div>
                    )}

                    {visibleTags.length === 0 ? (
                        <p className="tags-list__no-results">Nenhuma etiqueta encontrada para “{search}”.</p>
                    ) : (
                        <ul className="tags-list">
                            {visibleTags.map((tag) => (
                                <li className="tags-list__row" key={tag.id}>
                                    <button
                                        type="button"
                                        className="tags-list__main"
                                        onClick={() => openEdit(tag)}
                                        title="Editar etiqueta"
                                    >
                                        <TagChip name={tag.name} color={tag.color} />
                                    </button>
                                    <div className="tags-list__actions">
                                        <button
                                            type="button"
                                            className="tags-list__action"
                                            aria-label={`Editar etiqueta ${tag.name}`}
                                            title="Editar"
                                            onClick={() => openEdit(tag)}
                                        >
                                            <Icon name="edit" size={16} strokeWidth={2} />
                                        </button>
                                        <button
                                            type="button"
                                            className="tags-list__action tags-list__action--danger"
                                            aria-label={`Excluir etiqueta ${tag.name}`}
                                            title="Excluir"
                                            onClick={() => setToDelete(tag)}
                                        >
                                            <Icon name="delete" size={16} strokeWidth={2} />
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </Box>
            )}

            <Modal
                title={editing ? 'Editar etiqueta' : 'Nova etiqueta'}
                show={showModal}
                close={() => setShowModal(false)}
                footer={
                    <Button
                        type="primary"
                        size="small"
                        svg="save-white"
                        text="Salvar"
                        action={handleSave}
                        loading={saving}
                        disabled={saveDisabled}
                    />
                }
            >
                <div className="section">
                    <div className="tags-modal__preview">
                        <span className="tags-modal__preview-label">Pré-visualização</span>
                        <TagChip name={name.trim() === '' ? 'Nome da etiqueta' : name} color={color} />
                    </div>
                    <div className="row">
                        <div className="col-12">
                            <Input label="Nome *" value={name} change={setName} />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-12">
                            <InputColor label="Cor *" value={color} change={setColor} />
                        </div>
                    </div>
                </div>
            </Modal>

            <ModalDelete
                show={toDelete !== null}
                close={() => setToDelete(null)}
                action={handleDelete}
                loading={deleting}
                text={`Tem certeza que deseja excluir a etiqueta "${toDelete?.name}"? Ela será removida de todos os cards.`}
            />
        </PanelTemplate>
    );
};

export default CrmTags;
